import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  addView,
  decodeShareToken,
  downloadViewsFile,
  loadViews,
} from '../../lib/savedViews';

/**
 * Mount-once helper che gestisce:
 *  1. Shortcut globale Cmd/Ctrl+Shift+E → esporta tutte le viste salvate.
 *  2. Query param `?v=<token>` su qualsiasi route → propone di salvare la vista
 *     condivisa e poi pulisce l'URL.
 *
 * Si appoggia a `react-router-dom` per leggere/manipolare l'URL senza reload.
 */
export default function ShareAndShortcuts() {
  const navigate = useNavigate();
  const location = useLocation();
  // Evita di mostrare due volte il prompt per lo stesso token (es. React StrictMode)
  const handledRef = useRef('');

  // ── Hotkey: Cmd/Ctrl + Shift + E ────────────────────────────────────────────
  useEffect(() => {
    const onKey = (e) => {
      if (!(e.metaKey || e.ctrlKey) || !e.shiftKey) return;
      if (e.key.toLowerCase() !== 'e') return;
      // Ignora se l'utente sta scrivendo in un input/textarea/contentEditable
      const t = e.target;
      const tag = t?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || t?.isContentEditable) return;
      e.preventDefault();
      const count = loadViews().length;
      if (count === 0) {
        toast.info('Nessuna vista da esportare');
        return;
      }
      downloadViewsFile();
      toast.success(`Esportate ${count} viste`);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // ── Share token handler: legge ?v= e propone import ─────────────────────────
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token = params.get('v');
    if (!token) return;
    if (handledRef.current === token) return;
    handledRef.current = token;

    const view = decodeShareToken(token);
    params.delete('v');
    const cleanSearch = params.toString();

    if (!view) {
      toast.error('Link vista non valido');
      // Pulisci comunque l'URL
      navigate(`${location.pathname}${cleanSearch ? '?' + cleanSearch : ''}`, { replace: true });
      return;
    }

    // Se ho già la stessa vista (route+search), naviga senza prompt
    const incomingSearch = view.search || '';
    const already = loadViews().some(
      (v) => v.route === view.route && (v.search || '') === incomingSearch
    );

    // Target finale: applica route+search della vista condivisa, mantenendo
    // eventuali altri parametri già presenti nell'URL (oltre a ?v=)
    const targetParams = new URLSearchParams(cleanSearch);
    const sharedParams = new URLSearchParams(incomingSearch);
    for (const [k, val] of sharedParams.entries()) targetParams.set(k, val);
    const finalSearch = targetParams.toString();
    const target = `${view.route}${finalSearch ? '?' + finalSearch : ''}`;

    if (already) {
      toast.info(`Vista "${view.name}" già presente`);
      navigate(target, { replace: true });
      return;
    }

    const save = window.confirm(
      `Vista condivisa: "${view.name}"\n` +
      `Destinazione: ${view.route}${view.search ? '?' + view.search : ''}\n\n` +
      `OK = Salva nelle tue viste e apri\n` +
      `Annulla = Apri soltanto (senza salvare)`
    );
    if (save) {
      addView({ name: view.name, route: view.route, search: incomingSearch });
      toast.success(`Vista "${view.name}" salvata`);
    }
    navigate(target, { replace: true });
  }, [location.pathname, location.search, navigate]);

  return null;
}
